import { logout } from '../store/session';
import { store } from '../index';
import { csrfFetch } from './csrfFetch';

// Default timeout duration in milliseconds (4 hours)
// This is just a fallback; the actual value will be fetched from the server
const DEFAULT_TIMEOUT_DURATION = 4 * 60 * 60 * 1000;
// How often to refresh the server-side session (30 minutes)
const REFRESH_INTERVAL = 30 * 60 * 1000;
// Warning time before logout (30 seconds)
const WARNING_TIME = 30 * 1000;

// Custom event for warning dialog
export const SHOW_WARNING_EVENT = 'show-timeout-warning';

let timeoutId = null;
let warningTimeoutId = null;
let refreshIntervalId = null;
let timeoutDuration = DEFAULT_TIMEOUT_DURATION;
let lastActivityTime = Date.now();

// Function to refresh the server-side session
const refreshServerSession = async () => {
  try {
    const state = store.getState();
    if (state.session && state.session.user) {
      // Only refresh if the user is logged in and has been active recently
      const currentTime = Date.now();
      const timeSinceLastActivity = currentTime - lastActivityTime;

      // Only refresh if there has been activity in the last refresh interval
      if (timeSinceLastActivity < REFRESH_INTERVAL) {
        await csrfFetch('/api/auth/refresh-session', {
          method: 'POST',
        });
        console.log('Server session refreshed');
      }
    }
  } catch (error) {
    console.error('Error refreshing server session:', error);
  }
};

// Function to show the warning dialog
const showWarningDialog = () => {
  console.log('Session timeout: showing warning dialog');
  // Dispatch custom event to show warning dialog
  const event = new CustomEvent(SHOW_WARNING_EVENT);
  window.dispatchEvent(event);
};

// Function to perform logout
const performLogout = () => {
  // Clear all timeouts and intervals
  if (timeoutId) clearTimeout(timeoutId);
  if (warningTimeoutId) clearTimeout(warningTimeoutId);
  if (refreshIntervalId) clearInterval(refreshIntervalId);

  // Check if user is logged in before attempting logout
  const state = store.getState();
  if (state.session && state.session.user) {
    console.log('Session timeout: logging out due to inactivity');

    // Clear any warning dialogs still showing
    const event = new Event('hide-timeout-warning');
    window.dispatchEvent(event);

    store.dispatch(logout(true));
  }
};

// Function to reset the timeout timer
const resetTimer = () => {
  // Update last activity time
  lastActivityTime = Date.now();

  // Clear existing timeouts
  if (timeoutId) clearTimeout(timeoutId);
  if (warningTimeoutId) clearTimeout(warningTimeoutId);

  // Set timeout for warning dialog
  warningTimeoutId = setTimeout(showWarningDialog, timeoutDuration - WARNING_TIME);

  // Set timeout for logout
  timeoutId = setTimeout(performLogout, timeoutDuration);
};

// Function to start tracking user activity
export const startSessionTimeout = (customTimeoutDuration) => {
  // Allow custom timeout duration to be set
  if (customTimeoutDuration) {
    timeoutDuration = customTimeoutDuration;
  }

  // Reset timer on initial call
  resetTimer();

  // Set up event listeners for user activity
  const activityEvents = [
    'mousedown', 'mousemove', 'keypress',
    'scroll', 'touchstart', 'click', 'keydown'
  ];

  // Function to handle user activity
  const handleUserActivity = () => {
    resetTimer();
  };

  // Add event listeners
  activityEvents.forEach(event => {
    window.addEventListener(event, handleUserActivity);
  });

  // Set up interval to refresh the server-side session
  refreshIntervalId = setInterval(refreshServerSession, REFRESH_INTERVAL);

  // Return a cleanup function to remove event listeners
  return () => {
    if (timeoutId) clearTimeout(timeoutId);
    if (warningTimeoutId) clearTimeout(warningTimeoutId);
    if (refreshIntervalId) clearInterval(refreshIntervalId);

    activityEvents.forEach(event => {
      window.removeEventListener(event, handleUserActivity);
    });
  };
};

// Function to manually set timeout duration
export const setTimeoutDuration = (durationInMilliseconds) => {
  timeoutDuration = durationInMilliseconds;
  resetTimer();
};
